import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { ISocialAccount } from '@components';

type MemberWithSocialData = {
  member: MemberSearchQuery_members | null;
  socialData?: ISocialAccount | null;
};

export interface EngagementRateData {
  allPostsRate: number | null;
  sponsoredRate: number | null;
  allPostsBenchmark: number | null;
  sponsoredBenchmark: number | null;
  hasData: boolean;
  username: string;
}

export interface EngagementRateDataAdapter<T> {
  extractEngagementRateData(source: T): EngagementRateData;
}

export class MemberEngagementRateAdapter implements EngagementRateDataAdapter<MemberWithSocialData> {
  extractEngagementRateData(source: MemberWithSocialData): EngagementRateData {
    const { member, socialData } = source;

    // Try to get data from socialData first (for ProspectsOverview), then member (for other contexts)
    const dataSource = socialData || (member as any);

    if (!dataSource) {
      return {
        allPostsRate: null,
        sponsoredRate: null,
        allPostsBenchmark: null,
        sponsoredBenchmark: null,
        hasData: false,
        username: '',
      };
    }

    const allPostsRate = this.extractEngagementRatio(dataSource);
    const sponsoredRate = this.extractSponsoredEngagementRatio(dataSource);
    const allPostsBenchmark = this.extractEngagementRatioBenchmark(dataSource);
    const sponsoredBenchmark = this.extractSponsoredEngagementRatioBenchmark(dataSource);

    const hasData = allPostsRate !== null || sponsoredRate !== null;
    const username = this.extractUsername(dataSource, member);

    return {
      allPostsRate,
      sponsoredRate,
      allPostsBenchmark,
      sponsoredBenchmark,
      hasData,
      username,
    };
  }

  private extractEngagementRatio(dataSource: any): number | null {
    return dataSource?.engagement_ratio ?? null;
  }

  private extractSponsoredEngagementRatio(dataSource: any): number | null {
    return dataSource?.sponsored_engagement_ratio ?? null;
  }

  private extractEngagementRatioBenchmark(dataSource: any): number | null {
    return dataSource?.engagement_ratio_benchmark ?? null;
  }

  private extractSponsoredEngagementRatioBenchmark(dataSource: any): number | null {
    return dataSource?.sponsored_engagement_ratio_benchmark ?? null;
  }

  private extractUsername(dataSource: any, member: MemberSearchQuery_members | null): string {
    return dataSource?.username || member?.email?.split('@')[0] || '';
  }
}

/**
 * Default member engagement rate adapter instance
 */
export const memberEngagementRateAdapter = new MemberEngagementRateAdapter();
